import { useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { useSEO } from '@/lib/useSEO';
import { GOOGLE_PLAY_URL, APP_STORE_URL, detectPlatform } from '@/lib/appLinks';
import { isNativeApp } from '@/lib/native';
import { Smartphone, Apple, Play, Clock, ShieldCheck, Search, Truck } from 'lucide-react';

const PERKS = [
  { icon: Search, text: 'Encuentra repuestos por marca, referencia o vehículo' },
  { icon: ShieldCheck, text: 'Vendedores verificados y pago seguro' },
  { icon: Truck, text: 'Sigue tus pedidos hasta la puerta de tu casa' },
];

function StoreButton({ store, highlight }) {
  const isPlay = store === 'android';
  const url = isPlay ? GOOGLE_PLAY_URL : APP_STORE_URL;
  const Icon = isPlay ? Play : Apple;
  const label = isPlay ? 'Google Play' : 'App Store';
  const testid = isPlay ? 'download-google-play-btn' : 'download-app-store-btn';
  const base = 'flex items-center gap-4 w-full sm:w-72 px-6 py-4 border-2 transition-[transform,background-color,border-color] duration-200';

  if (!url) {
    return (
      <div data-testid={testid} aria-disabled="true"
        className={`${base} border-slate-300 bg-slate-100 text-slate-400 cursor-not-allowed`}>
        <Icon className="h-8 w-8 shrink-0" />
        <span className="text-left leading-tight">
          <span className="block text-xs uppercase tracking-widest">Próximamente en</span>
          <span className="block text-xl font-bold">{label}</span>
        </span>
      </div>
    );
  }
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" data-testid={testid}
      className={`${base} hover:-translate-y-0.5 ${highlight
        ? 'border-orange-500 bg-orange-500 text-slate-900 hover:bg-orange-400'
        : 'border-slate-900 bg-slate-900 text-white hover:bg-slate-800'}`}>
      <Icon className="h-8 w-8 shrink-0" />
      <span className="text-left leading-tight">
        <span className="block text-xs uppercase tracking-widest">Descargar en</span>
        <span className="block text-xl font-bold">{label}</span>
      </span>
    </a>
  );
}

export default function Descargar() {
  const platform = detectPlatform();
  const storeUrl = isNativeApp() ? null : { android: GOOGLE_PLAY_URL, ios: APP_STORE_URL }[platform];
  useEffect(() => {
    // replace(): el botón «atrás» no vuelve a esta página y evita un bucle de redirección.
    if (storeUrl) window.location.replace(storeUrl);
  }, [storeUrl]);
  useSEO({
    title: 'Descarga la app | AutoMarket Colombia',
    description: 'Descarga AutoMarket Colombia en tu celular y compra repuestos para tu vehículo con vendedores verificados.',
    canonical: 'https://automarketcolombia.co/descargar',
  });
  const order = platform === 'ios' ? ['ios', 'android'] : ['android', 'ios'];

  return (
    <Layout>
      <section data-testid="download-page" data-platform={platform} className="bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-16 sm:py-24 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-orange-400">
              <Smartphone className="h-4 w-4" /> App oficial
            </span>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter">
              AutoMarket Colombia<br /><span className="text-orange-500">en tu bolsillo.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-slate-300 max-w-xl">
              El marketplace de repuestos para vehículos, ahora en tu celular.
            </p>
            <ul className="mt-8 space-y-3">
              {PERKS.map(({ icon: I, text }) => (
                <li key={text} className="flex items-center gap-3 text-slate-200 text-sm sm:text-base">
                  <I className="h-5 w-5 text-orange-500 shrink-0" /> {text}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white text-slate-900 p-8 border-t-4 border-orange-500" data-testid="download-card">
            {storeUrl && (
              <p data-testid="download-redirecting-msg" className="mb-4 text-sm text-slate-600">
                Abriendo la tienda… Si no se abre, toca el botón.
              </p>
            )}
            <h2 className="text-base md:text-lg font-bold">
              {platform === 'android' && 'Tu celular es Android'}
              {platform === 'ios' && 'Tu celular es iPhone'}
              {platform === 'other' && 'Elige tu tienda'}
            </h2>
            <div className="mt-6 flex flex-col gap-4">
              {order.map((s) => <StoreButton key={s} store={s} highlight={s === platform} />)}
            </div>
            {platform === 'ios' && !APP_STORE_URL && (
              <p data-testid="ios-coming-soon-msg" className="mt-6 flex gap-2 text-sm text-slate-600">
                <Clock className="h-4 w-4 mt-0.5 shrink-0 text-orange-600" />
                La app para iPhone está en revisión de Apple y estará disponible muy pronto.
                Mientras tanto puedes comprar desde automarketcolombia.co en tu navegador.
              </p>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
